import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getErrorMessage } from '@/utils/errors';
import { RootState } from '../index';

/**
 * Creative generation jobs — the durable, live-tracked state behind the "generating…" tray.
 *
 * The spark-creatives service runs generations asynchronously and persists each one, so a job
 * survives navigating away, a reload, and re-login. This slice mirrors that: it rehydrates from
 * the jobs list on load, then the owner-hub websocket pushes every state change as a `creative_job`
 * event (handled in `store/ws/ws.js`). Owner-scoped, so it is cleared on session teardown.
 */

const DEBUG = false;

/** The spark-creatives service base (`…/api/spark-creatives/v1`), or '' when unset. */
const creativesBase = (): string => {
  const cfg = window.config as unknown as Record<string, unknown>;
  const url = typeof cfg?.creativesApiUrl === 'string' ? (cfg.creativesApiUrl as string).replace(/\/+$/, '') : '';
  return url;
};

export type CreativeJobStatus = 'queued' | 'running' | 'done' | 'failed' | string;

export interface CreativeJob {
  id: string;
  tenant_id: string;
  placement?: string;
  /** generate | edit | publish | formats | video | storyboard | banner_kit | playable_assets | kit_layers |
   *  landing_page, so the UI can label the run (e.g. "Publishing…"). */
  kind?: string;
  /** Which banner the run targets, so its card can show a busy overlay while it's in flight. */
  root_banner_id?: string;
  /** The VERSION a 'formats' run completes (its parent creative id) — anchors the in-thumb generating
   *  overlay to the exact version being filled, so it survives a page refresh. */
  parent_id?: string;
  status: CreativeJobStatus;
  prompt?: string;
  result_url?: string;
  creative_id?: string;
  error?: string;
  /** Per-format progress for a multi-slot draw (generate / formats): slots done and the target. The
   *  backend streams + persists these, so `total > 0` drives a determinate, reload-safe progress bar. */
  completed?: number;
  total?: number;
  /** Storyboard run only: index-aligned DISPLAY image URLs, populated incrementally as the run
   *  progresses. `frames[0]` is the key visual; `frames[i]` (i=1..N) is beat i's end-frame image URL.
   *  Delivered on BOTH the polled jobs list and the `creative_job` ws update. Volatile (see mergeJob):
   *  a partial frame carrying it REPLACES the stored array wholesale (the backend sends the full slice
   *  each tick); a frame omitting it leaves the stored one untouched. */
  frames?: string[];
  /** Storyboard run only: the id of the node currently being worked: `frame-<i>` | `beat-<i>`
   *  (1-based) | `assembling` | `done`, driving the canvas run choreography. Delivered on both the
   *  polled list and the ws update. Volatile (see mergeJob). */
  stage?: string;
  /** Storyboard run only: the fine-grained step being done RIGHT NOW, finer than `stage`: `<key>` or
   *  `<key>:<N>` (N = 1-based beat), e.g. `study`, `animate:2`, `handoff:1`, `assemble`. Status copy
   *  only (the canvas still follows `stage`); an unknown key falls back to the stage copy. Delivered on
   *  both the polled list and the ws update. Volatile (see mergeJob): an empty string clears it. */
  activity?: string;
  /** Storyboard run only: index-aligned CLIP urls, populated incrementally as beats complete.
   *  `segments[0]` is `''` (the key visual has no clip); `segments[i]` (i=1..N) is beat i's rendered
   *  clip URL. Delivered on BOTH the polled jobs list and the `creative_job` ws update. Volatile like
   *  `frames` (see mergeJob): NOT in IDENTITY_KEYS, so a frame carrying it REPLACES the stored array
   *  wholesale, and one omitting it leaves the stored one untouched. */
  segments?: string[];
  created_at?: string;
  updated_at?: string;
}

/** The list uses `id`; websocket `creative_job` updates use `job_id`. Normalise to `id`. */
type RawJob = Partial<CreativeJob> & { job_id?: string };

/** A job's IDENTITY fields never change once known (from the jobs-list rehydrate or the correct first
 *  frame), so a later live frame must not overwrite them: keeping the first known-good value makes the store
 *  immune to an upstream frame carrying a corrupted identity. This matters because the owner-hub relay has
 *  been observed to mangle `root_banner_id` under concurrent sends (the brand domain overwrites the UUID from
 *  ~the 3rd per-slot tick); letting that through re-keys the still-running job so it drops out of
 *  `activeRunsByRoot`, and the Studio loses its "generating" state mid-run even though `status` is 'running'. */
const IDENTITY_KEYS: (keyof CreativeJob)[] = ['id', 'tenant_id', 'root_banner_id', 'kind', 'parent_id', 'created_at'];

/** Merge a normalised job onto the stored one using ONLY its DEFINED, NON-IDENTITY fields. A live
 *  `creative_job` frame carries just what changed (a running tick has no created_at/prompt/result_url, and
 *  none carry created_at/updated_at at all), and `normalise` fills the rest as `undefined`; a plain spread
 *  would REPLACE, nulling persisted fields. Volatile fields (status/completed/total/result_url/…) update on
 *  every frame; identity fields are write-once (see IDENTITY_KEYS). A storyboard run's `frames`/`stage`/
 *  `activity` are volatile too (they are NOT in IDENTITY_KEYS): a frame carrying `frames` replaces the
 *  stored array wholesale, and `stage`/`activity` update, while a frame omitting one leaves it untouched.
 *  The jobs-list rehydrate stays a plain authoritative replace, so a bad value can still be corrected
 *  from the DB (source of truth). */
const mergeJob = (prev: CreativeJob | undefined, job: CreativeJob): CreativeJob => {
  const defined = Object.fromEntries(Object.entries(job).filter(([, v]) => v !== undefined)) as Partial<CreativeJob>;
  if (prev) {
    for (const k of IDENTITY_KEYS) {
      if (prev[k] != null && prev[k] !== '') delete defined[k]; // keep the first known-good identity value
    }
  }
  return { ...prev, ...defined } as CreativeJob;
};

const normalise = (raw: RawJob): CreativeJob | null => {
  const id = raw.id ?? raw.job_id;
  if (!id) return null;
  return {
    id,
    tenant_id: raw.tenant_id ?? '',
    placement: raw.placement,
    kind: raw.kind,
    root_banner_id: raw.root_banner_id,
    parent_id: raw.parent_id,
    status: raw.status ?? 'queued',
    prompt: raw.prompt,
    result_url: raw.result_url,
    creative_id: raw.creative_id,
    error: raw.error,
    completed: raw.completed,
    total: raw.total,
    // Coerce to absent (not null): the Go side may serialise a nil slice as `null`; passing that
    // through would survive mergeJob's `!== undefined` filter and NULL the stored frames on a partial
    // tick. Absent means "unchanged"; a real array replaces the stored one wholesale.
    frames: Array.isArray(raw.frames) ? raw.frames : undefined,
    stage: typeof raw.stage === 'string' ? raw.stage : undefined,
    activity: typeof raw.activity === 'string' ? raw.activity : undefined,
    // Coerced to absent (not null) exactly like `frames` above, so a partial tick omitting it never
    // NULLs the stored clips; a real array replaces the stored one wholesale (volatile, not identity).
    segments: Array.isArray(raw.segments) ? raw.segments : undefined,
    created_at: raw.created_at,
    updated_at: raw.updated_at,
  };
};

/** `refusal`: the service's own refusal code (a 409's `{"error": "<code>"}`), when it answered with one. */
type GenericError = { rejectValue: { error: string; refusal?: string } };

const authHeader = (state: RootState): Record<string, string> | undefined => {
  const token = state.authentication.access_token;
  return token ? { Authorization: token } : undefined;
};

/** Fetch the owner's recent jobs (rehydration on load). Optional brand filter. */
export const fetchCreativeJobs = createAsyncThunk<CreativeJob[], { tenantId?: number | string } | void, GenericError>(
  'creativeJobs/list',
  async (arg, { getState, rejectWithValue }) => {
    const base = creativesBase();
    if (!base) return [];
    try {
      const tenantId = arg && 'tenantId' in arg ? arg.tenantId : undefined;
      const response = await axios.get<{ data?: RawJob[] }>(
        `${base}/creatives/jobs${tenantId ? `?tenant_id=${encodeURIComponent(String(tenantId))}` : ''}`,
        { headers: authHeader(getState() as RootState), _owsNoAuthRecovery: true },
      );
      const rows = Array.isArray(response.data?.data) ? response.data.data : [];
      return rows.map(normalise).filter((j): j is CreativeJob => j !== null);
    } catch (error) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/** Result of enqueuing a generation. */
export interface EnqueueResult {
  ok: boolean;
  jobId?: string;
  needed?: number;
  balance?: number;
}

/** The generation jobs of the Banner Kits / Mini-games / Landing Pages entities: `banner_kit` (a kit's hero +
 *  per-shape renders), `playable_assets` (a mini-game's AI art), `kit_layers` (one kit shape decomposed into layers)
 *  and `landing_page` (a landing page's hero art, copy and game art, per `ad.ops`). Each targets one version of its
 *  entity (`ad.target_version_at`) and carries its parameters in the `ad` object. */
export type AdJobMediaType = 'banner_kit' | 'playable_assets' | 'kit_layers' | 'landing_page';

/** `image` (the default, existing behaviour), `video` (the video-generation vertical slice),
 *  `storyboard` (a multi-beat branded video ad), both gated by `creatives-video`, or one of the
 *  {@link AdJobMediaType} entity jobs (gated by `creatives-banner-kits` / `creatives-playables`). */
export type GenerateMediaType = 'image' | 'video' | 'storyboard' | AdJobMediaType;

/** Narrowing guard: whether a media type is one of the {@link AdJobMediaType} entity jobs. */
export const isAdJobMediaType = (mediaType: GenerateMediaType | undefined): mediaType is AdJobMediaType =>
  mediaType === 'banner_kit' || mediaType === 'playable_assets' || mediaType === 'kit_layers' || mediaType === 'landing_page';

/** The `ad` object of an {@link AdJobMediaType} generation request (spark-creatives `adRequest`). */
export interface AdGenerateRequest {
  /** banner_kit / kit_layers: the kit entity id. */
  kit_id?: string;
  /** playable_assets: the mini-game entity id. */
  playable_id?: string;
  /** landing_page: the landing page entity id. */
  landing_page_id?: string;
  /** landing_page: what the run draws and writes (`hero`, `copy`, `mechanic`); empty = all that apply. */
  ops?: string[];
  /** The created_at (RFC 3339) of the entity version this run fills. Required by every ad job. */
  target_version_at?: string;
  /** banner_kit only: narrow the run to these shape ids (a per-size redraw). */
  only_shapes?: string[];
  /** banner_kit only: the owner's redraw instruction for {@link only_shapes}. */
  instruction?: string;
  /** kit_layers only: the shape id to decompose into layers. */
  shape?: string;
}

/** Target aspect ratio for a video generation. */
export type VideoAspectRatio = '16:9' | '9:16' | '1:1' | '4:5' | '2:3';

/** A storyboard beat's dramatic role, in order of a typical ad arc. */
export type StoryboardBeatRole = 'hook' | 'offer' | 'proof' | 'cta';

/** How a beat's END frame (F_{i+1}) is obtained: generated from a text description (edited from the
 *  key visual for consistency) or taken from an existing brand image URL. */
export type StoryboardEndFrame = { source: 'generate'; prompt: string } | { source: 'url'; url: string };

/** One beat of a storyboard ad. Beat i animates F_i -> F_{i+1}; F_0 is the promotion key visual
 *  (`base_image_url`), and F_{i+1} is this beat's `end_frame`. */
export interface StoryboardBeatInput {
  role: StoryboardBeatRole;
  duration_seconds: number;
  motion_intent: string;
  end_frame: StoryboardEndFrame;
  /** 0-based index into the storyboard's promotions of the promotion this beat advertises. Absent/0 for a
   *  single-promo storyboard. */
  promotion_index?: number;
}

/** One promotion advertised in a storyboard (multi-promo): its key visual + its on-screen offer copy.
 *  promotions[0] is the run's F0 (== base_image_url). */
export interface StoryboardPromotion {
  banner_id: string;
  image_url: string;
  headline?: string;
  subtitle?: string;
  cta_text?: string;
}

/** Brand identity baked into the ad (the logo bug and the branded end-card). */
export interface StoryboardBrandInput {
  name: string;
  domain?: string;
  logo_url?: string;
  cta_text?: string;
}

/** Which generation pipeline a storyboard ad uses: `beats` (v1, one clip per beat, stitched) or `single_clip`
 *  (v2, ONE clip from ONE timestamped shot-plan prompt). Absent on the wire = `beats`. */
export type StoryboardPipeline = 'beats' | 'single_clip';

/** What a storyboard ad advertises (mirrors the nova `VideoSubjectType`; declared here so the store never
 *  imports from a page module). */
export type StoryboardSubjectType = 'promotion' | 'game' | 'gamification';

/** One recurring character/prop of a v2 shot plan, described in text. `source` records which image the role
 *  comes from (for a future reference-image mode). */
export interface ShotPlanCast {
  id: string;
  description: string;
  source: 'key_visual' | 'mood' | 'text';
}

/** One timestamped shot of a v2 shot plan. Integer seconds, contiguous from 0 to the clip length; exactly
 *  one shot (the last) is the `closing` backdrop the logo + CTA are composited over. */
export interface ShotPlanShot {
  start_s: number;
  end_s: number;
  action: string;
  camera: string;
  sound: string;
  closing?: boolean;
}

/** The model-agnostic v2 storyboard: a style, a world, one music line, a small cast and 2..8 timed shots.
 *  The backend adapter compiles it into the chosen video model's prompt. */
export interface ShotPlan {
  style: string;
  world: string;
  music: string;
  cast: ShotPlanCast[];
  shots: ShotPlanShot[];
}

/** What a v2 generation WILL send to the video model (the adapter's request summary). `null` = the field is
 *  omitted for that model. */
export interface ClipRequestSummary {
  model: string;
  duration_seconds?: number | null;
  aspect_ratio?: string | null;
  resolution?: string | null;
  generate_audio?: boolean | null;
  opening_frame?: boolean | null;
}

/** The `storyboard` object of a storyboard generation request body. */
export interface StoryboardInput {
  template_id: string;
  /** Owner-selected video model id, passed through to the content-generation proxy. Omitted =
   *  the backend's configured default. */
  video_model?: string;
  /** Refine only (media_type `storyboard-refine`): the FIRST promotion's exact headline/subtitle, which the
   *  LLM must keep verbatim as on-screen text (compat for the single-promo handler fields). Ignored by a real
   *  generation. */
  headline?: string;
  subtitle?: string;
  brand: StoryboardBrandInput;
  /** The promotions this storyboard advertises (multi-promo). Each beat's promotion_index points into it.
   *  Absent/one = single-promo. A single-clip ad carries exactly one. */
  promotions?: StoryboardPromotion[];
  /** v1 beats. Always `[]` for a single-clip ad. */
  beats: StoryboardBeatInput[];
  /** The owner's chosen total ad length. v1: 10/15/20/30, steers the refine LLM's beat count/durations.
   *  Single clip: the exact clip length L (one of the model's clip lengths). */
  target_duration_seconds?: number;
  /** `single_clip` for a v2 ad; omitted (= beats) for v1. */
  pipeline?: StoryboardPipeline;
  /** Single clip only: the timestamped shot plan the clip is generated from. */
  shot_plan?: ShotPlan;
  /** What the ad advertises (persisted, and shapes the story). */
  subject_type?: StoryboardSubjectType;
  /** The owner's free-text prompt (may be empty). Distinct from the request's required top-level `prompt`. */
  prompt?: string;
  /** Single clip only: the English vibe brief (template title + its arc lines, at most 800 chars). */
  vibe?: string;
  /** v1 only: the ONE ad-level instrumental music brief the compose wrote (genre, tempo, key, instruments,
   *  energy arc), shared by every beat so the soundtrack stays one continuous piece. Omitted when empty (the
   *  backend then uses its default brief) and never sent for a single clip. */
  music?: string;
  /** The ad's language: a brand website language id (`mx`, `br`, `en`...). The promotions' copy is already in it;
   *  the compose writes the call to action in it. Omitted when empty (an older ad, rendered as before). */
  language?: string;
}

/** Args accepted by {@link enqueueGeneration} (and its pure body builder {@link buildGenerateRequestBody}). */
export interface EnqueueGenerationArgs {
  tenantId: number | string;
  placement: string;
  prompt: string;
  bannerId?: string;
  baseImageUrl?: string;
  /** Owner-supplied reference images (CDN urls). For an image request the model reproduces them faithfully;
   *  for a storyboard they are the mood board that steers the whole video's look/feel. */
  referenceImageUrls?: string[];
  /** The exact version this regeneration branched from (for lineage). */
  parentVersionId?: string;
  /** The brand's own site domain, so the service resolves its live guest token to read the
   *  banner's formats (the per-slot draw count it charges for) — same as the creatives list read. */
  domain?: string;
  /** Omitted/`'image'` keeps today's image-generation body untouched; `'video'` switches to the
   *  video pipeline's request shape (see {@link buildGenerateRequestBody}). */
  mediaType?: GenerateMediaType;
  /** Video/storyboard only: the target clip aspect ratio. */
  aspectRatio?: VideoAspectRatio;
  /** Video only: the target clip length, in seconds. */
  durationSeconds?: number;
  /** Storyboard only: the beats, brand and template of the ad (see {@link buildGenerateRequestBody}). */
  storyboard?: StoryboardInput;
  /** Storyboard regenerate/resume only: the source storyboard job id to branch from. When set with
   *  {@link fromBeat}, the backend reuses beats 1..K-1 and re-runs K..N (downstream cascades), charging
   *  only for the re-run beats. Omitted for a fresh generation. */
  resumeOf?: string;
  /** Storyboard regenerate/resume only: the 1-based beat K to re-run from (paired with {@link resumeOf}). */
  fromBeat?: number;
  /** Storyboard only: the created_at key of the existing storyboard-only version this render should FILL IN
   *  PLACE (so composing then generating stays ONE version). Omitted prepends a fresh render version. */
  targetVersionAt?: string;
  /** {@link AdJobMediaType} runs only: the entity, target version and run options (see {@link AdGenerateRequest}). */
  ad?: AdGenerateRequest;
  /** Which of the brand's mascots (its ACTIVE art style's cast) the run draws: `'none'`, or a mascot's id.
   *  Omitted or `''` = auto (the service rotates across the cast). Every media type that draws art, the ad runs
   *  `banner_kit` and `playable_assets` included; never `kit_layers` (a split of an existing render). */
  mascotId?: string;
  /** Image requests only: draw from the banner's casino-creation definition (`use_definition`). A fresh draw from
   *  it sends no `baseImageUrl`. */
  useDefinition?: boolean;
  /** Which of the brand's art styles (any status, with a definition) the run draws in (`style_id`). Omitted or `''` =
   *  the brand's active style, as before. Every media type that draws art, the ad runs `banner_kit`,
   *  `playable_assets` and `landing_page` included; never `kit_layers` (a split of an existing render). */
  styleId?: string;
}

/** The `POST {creativesApiUrl}/creatives/generate` request body. Image requests target ONE
 *  existing banner via `banner_id`/`base_image_url` (so the backend image-edits it, keeping it as
 *  the basis, rather than generating a fresh one) and may carry `reference_image_urls`; a video
 *  request carries the `media_type`/`aspect_ratio`/`duration_seconds` discriminator instead and
 *  never a `banner_id` (a video run is never an in-place edit of an existing banner). */
export type GenerateRequestBody =
  | {
      tenant_id: string;
      placement: string;
      prompt: string;
      banner_id?: string;
      base_image_url?: string;
      reference_image_urls?: string[];
      parent_version_id?: string;
      mascot_id?: string;
      use_definition?: boolean;
      style_id?: string;
    }
  | {
      tenant_id: string;
      placement: string;
      prompt: string;
      base_image_url?: string;
      parent_version_id?: string;
      media_type: 'video';
      aspect_ratio?: VideoAspectRatio;
      duration_seconds?: number;
      mascot_id?: string;
      style_id?: string;
    }
  | {
      tenant_id: string;
      placement: string;
      prompt: string;
      base_image_url?: string;
      parent_version_id?: string;
      media_type: 'storyboard';
      aspect_ratio?: VideoAspectRatio;
      storyboard: StoryboardInput;
      /** Owner-supplied mood-board reference images (CDN urls). Top-level (not nested in `storyboard`) to
       *  match the backend, which reads them off the request root for BOTH generate and refine. */
      reference_image_urls?: string[];
      /** Present only on a regenerate/resume: the source job id + 1-based beat K to re-run from. Both
       *  keys are OMITTED entirely on a fresh generation (see the conditional spread in the builder). */
      resume_of?: string;
      from_beat?: number;
      /** The created_at of the storyboard-only version this render fills in place. Omitted when the render
       *  should prepend a fresh version (see the conditional spread in the builder). */
      target_version_at?: string;
      /** The mascot pick (see {@link EnqueueGenerationArgs.mascotId}); absent = auto. */
      mascot_id?: string;
      /** The picked art style (see {@link EnqueueGenerationArgs.styleId}); absent = the brand's active one. */
      style_id?: string;
    }
  | {
      tenant_id: string;
      placement: string;
      prompt: string;
      media_type: AdJobMediaType;
      ad: AdGenerateRequest;
      /** banner_kit / playable_assets / landing_page: the mascot pick (see {@link EnqueueGenerationArgs.mascotId});
       *  absent = auto. */
      mascot_id?: string;
      /** The same ad runs: the picked art style (see {@link EnqueueGenerationArgs.styleId}); absent = the active one. */
      style_id?: string;
    };

/** Pure: builds the `/creatives/generate` request body from {@link EnqueueGenerationArgs}. For a
 *  non-video request this is EXACTLY today's body (no `media_type` key at all, not even
 *  `undefined`) — existing image generation must stay byte-for-byte unchanged. */
export const buildGenerateRequestBody = (args: EnqueueGenerationArgs): GenerateRequestBody => {
  const { tenantId, placement, prompt, bannerId, baseImageUrl, referenceImageUrls, parentVersionId, mediaType, aspectRatio, durationSeconds, storyboard, resumeOf, fromBeat, targetVersionAt, ad, mascotId, useDefinition, styleId } = args;
  // The mascot pick rides the request root, only when one is made (auto keeps each body exactly as before).
  const mascot = mascotId ? { mascot_id: mascotId } : {};
  // So does the picked art style, only when one is sent (none keeps each body exactly as before).
  const style = styleId ? { style_id: styleId } : {};
  // An entity job ALWAYS takes the ad body, `ad` or not: falling through to the image body (no media_type) would
  // start a charged image generation instead. A missing `ad` is sent empty so the backend rejects it (400). The
  // drawing ad runs carry the mascot pick; a layer split (`kit_layers`) draws nothing new, so it never does.
  if (isAdJobMediaType(mediaType)) {
    return {
      tenant_id: String(tenantId),
      placement,
      prompt,
      media_type: mediaType,
      ad: ad ?? {},
      ...(mediaType === 'kit_layers' ? {} : { ...mascot, ...style }),
    };
  }
  if (mediaType === 'storyboard' && storyboard) {
    // `resume_of`/`from_beat`/`target_version_at` are conditionally spread so a fresh generation's body stays
    // byte-clean (the keys are absent, not `undefined`); the backend reuses beats 1..K-1 and re-runs K..N.
    return {
      tenant_id: String(tenantId),
      placement,
      prompt,
      base_image_url: baseImageUrl,
      parent_version_id: parentVersionId,
      media_type: 'storyboard',
      aspect_ratio: aspectRatio,
      storyboard,
      // Mood-board refs ride the request root (undefined serialises away when none are attached).
      reference_image_urls: referenceImageUrls,
      ...(resumeOf !== undefined ? { resume_of: resumeOf } : {}),
      ...(fromBeat !== undefined ? { from_beat: fromBeat } : {}),
      ...(targetVersionAt ? { target_version_at: targetVersionAt } : {}),
      ...mascot,
      ...style,
    };
  }
  if (mediaType === 'video') {
    return {
      tenant_id: String(tenantId),
      placement,
      prompt,
      base_image_url: baseImageUrl,
      parent_version_id: parentVersionId,
      media_type: 'video',
      aspect_ratio: aspectRatio,
      duration_seconds: durationSeconds,
      ...mascot,
      ...style,
    };
  }
  // Exactly today's object, key order included: `banner_id` / `base_image_url` target ONE existing
  // banner (so the backend image-edits it, keeping it as the basis, rather than generating a fresh
  // one). `reference_image_urls` are owner uploads reproduced faithfully; `parent_version_id`
  // records the exact source version. Absent banner_id = a new creative for the placement. A definition draw and a
  // picked style ride along only when asked for, so every other body stays exactly as before.
  return {
    tenant_id: String(tenantId),
    placement,
    prompt,
    banner_id: bannerId,
    base_image_url: baseImageUrl,
    reference_image_urls: referenceImageUrls,
    parent_version_id: parentVersionId,
    ...mascot,
    ...(useDefinition ? { use_definition: true } : {}),
    ...style,
  };
};

/** Pure: the optimistic upsert's `kind`, derived from the media type: `'video'` for a video generation,
 *  `'storyboard'` for a storyboard ad, the media type itself for an {@link AdJobMediaType} entity job (the job
 *  kind equals it), `'generate'` otherwise (today's default). */
export const optimisticKindFor = (mediaType?: GenerateMediaType): 'generate' | 'video' | 'storyboard' | AdJobMediaType =>
  isAdJobMediaType(mediaType)
    ? mediaType
    : mediaType === 'storyboard'
      ? 'storyboard'
      : mediaType === 'video'
        ? 'video'
        : 'generate';

/** Enqueue a generation. Returns immediately with a job id (the worker runs it); a 402 surfaces
 *  the insufficient-credits answer so the caller can open Buy-credits. */
export const enqueueGeneration = createAsyncThunk<EnqueueResult, EnqueueGenerationArgs, GenericError>(
  'creativeJobs/enqueue',
  async (args, { getState, dispatch, rejectWithValue }) => {
    const { tenantId, placement, prompt, bannerId, domain, mediaType } = args;
    const base = creativesBase();
    if (!base) return rejectWithValue({ error: 'creatives service not configured' });
    try {
      const query = domain ? `?domain=${encodeURIComponent(domain)}` : '';
      const response = await axios.post<{ ok?: boolean; job_id?: string; status?: string; needed?: number; balance?: number }>(
        `${base}/creatives/generate${query}`,
        buildGenerateRequestBody(args),
        {
          headers: authHeader(getState() as RootState),
          _owsNoAuthRecovery: true,
          validateStatus: (s) => (s >= 200 && s < 300) || s === 402,
        },
      );
      if (response.status === 402) {
        return { ok: false, needed: response.data?.needed, balance: response.data?.balance };
      }
      const jobId = response.data?.job_id;
      if (jobId) {
        // Optimistically show it as queued right away; the live push then drives it. Carry
        // `root_banner_id` + `kind` so the targeted banner's card shows its busy overlay from the
        // instant of enqueue (not only once the first websocket update lands). A video run (and an
        // entity job: banner kit, mini-game art, kit layers) is never an in-place edit of an existing
        // banner, so it carries no `root_banner_id`.
        dispatch(
          upsertJob({
            id: jobId,
            tenant_id: String(tenantId),
            placement,
            prompt,
            root_banner_id:
              mediaType === 'video' || mediaType === 'storyboard' || isAdJobMediaType(mediaType) ? undefined : bannerId,
            kind: optimisticKindFor(mediaType),
            status: response.data?.status ?? 'queued',
          }),
        );
      }
      return { ok: true, jobId };
    } catch (error) {
      // A refusal (e.g. 409 `banner_definition_unavailable`) keeps its code so the composer can say why.
      const code = axios.isAxiosError(error) ? (error.response?.data as { error?: unknown } | undefined)?.error : undefined;
      return rejectWithValue({ error: getErrorMessage(error), ...(typeof code === 'string' && code ? { refusal: code } : {}) });
    }
  },
);

/** Args for {@link cancelJob}: the tenant and the in-flight job to stop. */
export interface CancelJobArgs {
  tenantId: number;
  jobId: string;
}

/**
 * Stop an in-flight (queued or running) generation and refund it (the studio "Stop" button). The backend
 * aborts the upstream work, refunds the credits and marks the run stopped, then broadcasts it. We ALSO flip
 * the job to a cancelled state optimistically so the editor drops out of its active/progress view the instant
 * Stop is pressed, without waiting for the websocket push. A 404/409 (already finished / gone) is treated as
 * success too: from the UI's point of view the run is simply no longer active. The `cancelled_by_owner` error
 * marker lets the editor show a neutral "Stopped" state instead of a failure (see `isCancelled`).
 */
export const cancelJob = createAsyncThunk<{ ok: boolean }, CancelJobArgs, GenericError>(
  'creativeJobs/cancel',
  async ({ tenantId, jobId }, { getState, dispatch, rejectWithValue }) => {
    const base = creativesBase();
    if (!base) return rejectWithValue({ error: 'creatives service not configured' });
    try {
      const res = await axios.post(
        `${base}/creatives/tenant/${tenantId}/jobs/${encodeURIComponent(jobId)}/cancel`,
        {},
        {
          headers: authHeader(getState() as RootState),
          _owsNoAuthRecovery: true,
          validateStatus: (s) => (s >= 200 && s < 300) || s === 404 || s === 409,
        },
      );
      if (res.status >= 200 && res.status < 300) {
        // The backend actually stopped + refunded it: flip to the cancelled state now so the editor drops the
        // run immediately (the websocket push then confirms).
        dispatch(upsertJob({ id: jobId, status: 'failed', error: 'cancelled_by_owner' }));
        return { ok: true };
      }
      // 404 (gone) / 409 (already finished or not stoppable): the backend did NOT cancel or refund, so do NOT
      // fabricate a "Stopped/refunded" state. Reconcile the true terminal status from the server instead (a
      // finished run shows its ad; an already-stopped one shows Stopped).
      void dispatch(fetchCreativeJobs({ tenantId }));
      return { ok: false };
    } catch (error) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/** Args for {@link refineStoryboard}. The base beats are rewritten by the LLM into a higher-converting
 *  storyboard; the promo copy is passed so the LLM keeps the offer text verbatim. */
export interface RefineStoryboardArgs {
  tenantId: number | string;
  templateId: string;
  brandName: string;
  headline?: string;
  subtitle?: string;
  cta?: string;
  baseImageUrl: string;
  aspectRatio: VideoAspectRatio;
  /** The promotions this storyboard advertises (multi-promo). The LLM auto-distributes them across beats.
   *  headline/subtitle/cta/baseImageUrl above are the FIRST promotion's (compat for the single-promo handler
   *  fields). Absent/one = single-promo. */
  promotions?: StoryboardPromotion[];
  beats: StoryboardBeatInput[];
  /** The backend requires a non-empty prompt on /creatives/generate (the template title). */
  prompt: string;
  /** Owner-supplied mood-board reference images (CDN urls), so the compose LLM writes the beats with
   *  the intended look/feel in mind. Sent top-level, mirroring the generate request. */
  referenceImageUrls?: string[];
  domain?: string;
  /** The chosen video model, so the compose LLM applies that model's prompt-engineering guidance
   *  (Seedance/Veo/Omni are steered differently). Maps to storyboard.video_model on the request. */
  videoModel?: string;
  /** The owner's chosen total ad length (10/15/20/30s). The beats are AUTHORED here, so this must ride the
   *  refine request (not just generate) for the compose LLM to size the beat count/durations to it. Omitted =
   *  Auto (the LLM picks). Maps to storyboard.target_duration_seconds. Single clip: the exact clip length L. */
  targetDurationSeconds?: number;
  /** `single_clip` composes a v2 shot plan (see spec 4.3); omitted/`beats` keeps today's v1 refine. */
  pipeline?: StoryboardPipeline;
  /** What the ad advertises (storyboard.subject_type). */
  subjectType?: StoryboardSubjectType;
  /** The owner's own free-text prompt, possibly empty (storyboard.prompt). The top-level `prompt` above stays
   *  non-empty (owner prompt or the template title). */
  ownerPrompt?: string;
  /** Single clip only: the English vibe brief (storyboard.vibe). */
  vibe?: string;
  /** The ad's language (storyboard.language): the compose writes the call to action in it. Empty or undefined = an
   *  older ad (the prompt is as before). */
  language?: string;
  /** Single clip only: the brand's logo + site domain, so the compose LLM derives the palette from the logo. */
  brandLogoUrl?: string;
  brandDomain?: string;
  /** The art style the ad will be drawn in (`style_id` on the request root), so the compose writes the beats in that
   *  style's world. Omitted or `''` = the brand's active style. */
  styleId?: string;
  /** Abort in-flight when the owner changes the promo/template again. */
  signal?: AbortSignal;
}

/** The compiled, model-specific single-clip prompt the backend adapter builds from a shot plan (what a
 *  generation WILL send). Returned by a v2 refine and by {@link compileStoryboard}. */
export interface CompiledClipPrompt {
  prompt: string;
  /** Only for models with a negative-prompt field (Veo); absent otherwise. */
  negativePrompt?: string;
  request?: ClipRequestSummary;
}

/** Result of a refine: the beats to use (already validated by the backend), and whether the LLM
 *  actually changed them (`false` = the base beats were returned unchanged, the fail-safe). */
export interface RefineStoryboardResult {
  beats: StoryboardBeatInput[];
  refined: boolean;
  /** A short, human-friendly ad name the compose LLM suggests from the offer/theme (empty on the fail-safe).
   *  The composer applies it only to a still-unnamed ad, so freshly composed ads are not left as "Untitled". */
  title?: string;
  /** The acquisition CTA the compose LLM authored for this cold-traffic ad (register/join/claim, never
   *  "Deposit"). Empty on the fail-safe. The composer applies it as the ad's CTA so the render uses it. */
  cta?: string;
  /** v1 only: the ad-level instrumental music brief the compose LLM wrote (hidden, no UI). Absent on the
   *  fail-safe or an older backend; the composer stores it on the draft and sends it back on generate. */
  music?: string;
  /** Single clip only: the composed shot plan (always present on a fulfilled v2 refine). */
  shotPlan?: ShotPlan;
  /** Single clip only: the adapter's compiled prompt for the chosen model, shown in "View final prompt". */
  compiled?: CompiledClipPrompt;
}

/** The rejectWithValue marker of a v2 refine answered WITHOUT a `shot_plan`: the backend predates single clip.
 *  The composer surfaces it explicitly (never a silent v1 fallback), pointing the owner at the Video Ads tab. */
export const SINGLE_CLIP_UNSUPPORTED = 'single_clip_unsupported';

/** Loose shape guard for a shot plan read off the wire (the backend validates it; this only rejects a missing
 *  or malformed object, e.g. an old backend's response). */
const isShotPlan = (value: unknown): value is ShotPlan => {
  if (!value || typeof value !== 'object') return false;
  const plan = value as Partial<ShotPlan>;
  return Array.isArray(plan.shots) && plan.shots.length > 0 && typeof plan.music === 'string';
};

/** A cast `source` as the backend reads it (`castSource`): a known value passes, anything else (absent) is `text`. */
const castSourceOf = (source: unknown): ShotPlanCast['source'] =>
  source === 'key_visual' || source === 'mood' ? source : 'text';

/** Fill the optional-on-the-wire fields of a shot plan read from the backend (Go `omitempty` drops an empty
 *  `world` / `sound`, a nil `cast` and, on a saved plan, an empty cast `id`), so every consumer (editable inputs
 *  and the fill-vs-new plan comparison included) sees the full shape. */
export const normaliseShotPlan = (plan: ShotPlan): ShotPlan => ({
  style: plan.style ?? '',
  world: plan.world ?? '',
  music: plan.music ?? '',
  cast: Array.isArray(plan.cast)
    ? plan.cast.map((c) => ({ id: c.id ?? '', description: c.description ?? '', source: castSourceOf(c.source) }))
    : [],
  shots: plan.shots.map((s) => ({
    start_s: s.start_s,
    end_s: s.end_s,
    action: s.action ?? '',
    camera: s.camera ?? '',
    sound: s.sound ?? '',
    ...(s.closing ? { closing: true } : {}),
  })),
});

/** Normalise the wire request summary (a missing / non-object value reads as absent). */
const readRequestSummary = (value: unknown): ClipRequestSummary | undefined =>
  value && typeof value === 'object' ? (value as ClipRequestSummary) : undefined;

/**
 * Seed-time storyboard refine: rides the same `/creatives/generate` route with `media_type:
 * 'storyboard-refine'` (free, synchronous, never enqueues a job), returning the LLM-refined beats. The
 * backend fails safe (returns the base beats on any error), and this thunk falls back to the passed-in
 * beats if the call itself fails — so the owner always ends up with a usable storyboard.
 */
export const refineStoryboard = createAsyncThunk<RefineStoryboardResult, RefineStoryboardArgs, GenericError>(
  'creativeJobs/refineStoryboard',
  async (args, { getState, rejectWithValue }) => {
    const singleClip = args.pipeline === 'single_clip';
    const base = creativesBase();
    // A single clip has no base beats to fall back to, so an unconfigured service is an error, not a no-op.
    if (!base) return singleClip ? rejectWithValue({ error: 'creatives service not configured' }) : { beats: args.beats, refined: false };
    try {
      const query = args.domain ? `?domain=${encodeURIComponent(args.domain)}` : '';
      const body = {
        tenant_id: String(args.tenantId),
        placement: 'storyboard',
        prompt: args.prompt,
        base_image_url: args.baseImageUrl,
        media_type: 'storyboard-refine',
        aspect_ratio: args.aspectRatio,
        // Mood-board refs top-level (the backend reads them off the request root), so the compose LLM sees them.
        reference_image_urls: args.referenceImageUrls,
        storyboard: {
          template_id: args.templateId,
          video_model: args.videoModel,
          // Single clip advertises exactly ONE subject.
          promotions: singleClip ? args.promotions?.slice(0, 1) : args.promotions,
          headline: args.headline,
          subtitle: args.subtitle,
          // v1 keeps today's {name, cta_text}; single clip adds the logo + domain so the compose LLM derives the
          // brand palette from the logo.
          brand: singleClip
            ? { name: args.brandName, domain: args.brandDomain, logo_url: args.brandLogoUrl, cta_text: args.cta }
            : { name: args.brandName, cta_text: args.cta },
          // A single clip is composed from scratch: the beats key is present and EMPTY (spec 4.3).
          beats: singleClip ? [] : args.beats,
          // v1: steers the beat count/durations (undefined => Auto). Single clip: the exact clip length L.
          target_duration_seconds: args.targetDurationSeconds,
          // What the ad advertises + the owner's own prompt (both pipelines: the v1 compose gains an OWNER
          // DIRECTION block from it). Undefined serialises away.
          subject_type: args.subjectType,
          prompt: args.ownerPrompt,
          // The ad's language, only when it has one (an older ad's compose request is unchanged).
          ...(args.language ? { language: args.language } : {}),
          ...(singleClip ? { pipeline: 'single_clip' as const, vibe: args.vibe } : {}),
        },
        // The picked art style rides the request root (as on generate), only when one is sent.
        ...(args.styleId ? { style_id: args.styleId } : {}),
      };
      const res = await axios.post<{
        ok?: boolean;
        refined?: boolean;
        beats?: StoryboardBeatInput[];
        title?: string;
        cta?: string;
        music?: unknown;
        shot_plan?: unknown;
        compiled_prompt?: string;
        negative_prompt?: string;
        request?: unknown;
      }>(`${base}/creatives/generate${query}`, body, {
        headers: authHeader(getState() as RootState),
        _owsNoAuthRecovery: true,
        signal: args.signal,
      });
      const title = (res.data?.title ?? '').trim();
      const cta = (res.data?.cta ?? '').trim();
      if (singleClip) {
        // An old backend ignores `pipeline` and answers the v1 shape (no shot_plan): surface that explicitly,
        // never fall back to v1 behind the owner's back.
        const plan = res.data?.shot_plan;
        if (!isShotPlan(plan)) return rejectWithValue({ error: SINGLE_CLIP_UNSUPPORTED });
        const negative = (res.data?.negative_prompt ?? '').trim();
        return {
          beats: [],
          refined: Boolean(res.data?.refined),
          title: title || undefined,
          cta: cta || undefined,
          shotPlan: normaliseShotPlan(plan),
          compiled: res.data?.compiled_prompt
            ? {
                prompt: res.data.compiled_prompt,
                negativePrompt: negative || undefined,
                request: readRequestSummary(res.data?.request),
              }
            : undefined,
        };
      }
      const beats = Array.isArray(res.data?.beats) && res.data.beats.length > 0 ? res.data.beats : args.beats;
      // The v1 ad-level music brief (omitted by the backend when empty; an older backend never sends it).
      const music = typeof res.data?.music === 'string' ? res.data.music.trim() : '';
      return {
        beats,
        refined: Boolean(res.data?.refined),
        title: title || undefined,
        cta: cta || undefined,
        music: music || undefined,
      };
    } catch (error) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/** Args for {@link compileStoryboard}: the same envelope as a refine plus the shot plan to compile. */
export interface CompileStoryboardArgs {
  tenantId: number | string;
  /** Non-empty (the backend requires it on /creatives/generate): the owner prompt or the template title. */
  prompt: string;
  baseImageUrl: string;
  aspectRatio: VideoAspectRatio;
  videoModel: string;
  targetDurationSeconds: number;
  shotPlan: ShotPlan;
  /** The ONE subject of the single-clip ad. */
  promotion: StoryboardPromotion;
  brand: StoryboardBrandInput;
  domain?: string;
  signal?: AbortSignal;
}

/**
 * Compile a single-clip shot plan into the chosen model's final prompt (spec 4.4): rides `/creatives/generate`
 * with `media_type: 'storyboard-compile'`. Free, synchronous, no LLM: the backend sanitises + validates the plan
 * (400 when invalid) and runs the per-model adapter. Powers the read-only "View final prompt" panel.
 */
export const compileStoryboard = createAsyncThunk<CompiledClipPrompt, CompileStoryboardArgs, GenericError>(
  'creativeJobs/compileStoryboard',
  async (args, { getState, rejectWithValue }) => {
    const base = creativesBase();
    if (!base) return rejectWithValue({ error: 'creatives service not configured' });
    try {
      const query = args.domain ? `?domain=${encodeURIComponent(args.domain)}` : '';
      const body = {
        tenant_id: String(args.tenantId),
        placement: 'storyboard',
        prompt: args.prompt,
        base_image_url: args.baseImageUrl,
        media_type: 'storyboard-compile',
        aspect_ratio: args.aspectRatio,
        storyboard: {
          pipeline: 'single_clip' as const,
          video_model: args.videoModel,
          target_duration_seconds: args.targetDurationSeconds,
          shot_plan: args.shotPlan,
          promotions: [args.promotion],
          brand: args.brand,
        },
      };
      // NOTE: the compile response names the prompt `prompt` (the refine names it `compiled_prompt`).
      const res = await axios.post<{ ok?: boolean; prompt?: string; negative_prompt?: string; request?: unknown }>(
        `${base}/creatives/generate${query}`,
        body,
        { headers: authHeader(getState() as RootState), _owsNoAuthRecovery: true, signal: args.signal },
      );
      const prompt = res.data?.prompt ?? '';
      if (!prompt) return rejectWithValue({ error: 'empty compile response' });
      const negative = (res.data?.negative_prompt ?? '').trim();
      return { prompt, negativePrompt: negative || undefined, request: readRequestSummary(res.data?.request) };
    } catch (error) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

export interface CreativeJobsState {
  byId: Record<string, CreativeJob>;
  loaded: boolean;
  streaming: boolean;
  error: string | null | undefined;
}

const initialState: CreativeJobsState = {
  byId: {},
  loaded: false,
  streaming: false,
  error: null,
};

export const creativeJobsSlice = createSlice({
  name: 'creativeJobs',
  initialState,
  reducers: {
    /** Merge one job (from a `creative_job` websocket push or an optimistic enqueue). */
    upsertJob(state, action: PayloadAction<RawJob>) {
      const job = normalise(action.payload);
      if (!job) return;
      DEBUG && console.log('[CreativeJobs] upsert', job.id, job.status);
      state.byId[job.id] = mergeJob(state.byId[job.id], job);
    },
    setStreaming(state, action: PayloadAction<boolean>) {
      state.streaming = action.payload;
    },
    /** Owner-scoped: cleared on session teardown (mirrors resetWallet / resetAiCredits). */
    resetCreativeJobs: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCreativeJobs.fulfilled, (state, action) => {
        state.loaded = true;
        state.error = null;
        // Authoritative rehydration from GET /creatives/jobs (each row carries every field, created_at
        // included), so a full replace is correct here; mergeJob is only for the PARTIAL websocket frames.
        for (const job of action.payload) state.byId[job.id] = { ...state.byId[job.id], ...job };
      })
      .addCase(fetchCreativeJobs.rejected, (state, action) => {
        state.loaded = true;
        state.error = action.payload?.error;
      });
  },
});

export const { upsertJob, setStreaming, resetCreativeJobs } = creativeJobsSlice.actions;

/* ---------- selectors ---------- */

/** The jobs map (a stable reference until a job changes). Consumers that need the sorted list
 *  should select THIS and derive with `sortJobsNewestFirst` under a `useMemo`, so an
 *  always-mounted reader (the tray) does not re-render on every unrelated store dispatch. */
export const selectCreativeJobsMap = (state: RootState): Record<string, CreativeJob> => state.creativeJobs.byId;

/** All jobs in a map, newest first. Pure — memoise at the call site. */
export const sortJobsNewestFirst = (byId: Record<string, CreativeJob>): CreativeJob[] =>
  Object.values(byId).sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? ''));

/** In-flight jobs (queued / running). Pure — memoise at the call site. */
export const activeJobs = (jobs: CreativeJob[]): CreativeJob[] =>
  jobs.filter((j) => j.status === 'queued' || j.status === 'running');

/** The job kind of an owner Art Style sample draw (Website > Art Style). These ride the same jobs list and
 *  `creative_job` events as the banner runs, but belong to the Art Style page only. */
export const ART_STYLE_SAMPLE_KIND = 'art_style_sample';

/** The job kind of an owner Art Style extraction (free; the style id in `root_banner_id`, placement `extract`, the
 *  model id in `prompt`, never shown). Once done it has queued the style's four sample draws as its children
 *  (`parent_id` = this job). Like the samples, it belongs to the Art Style page only. */
export const ART_STYLE_EXTRACT_KIND = 'art_style_extract';

/** The website-art job kinds: the site regeneration (Art Style), the category cards' regeneration and a website
 *  image's redraw (Website Creatives' Above the fold). Their pages follow them from the jobs map themselves. */
const WEBSITE_ART_JOB_KINDS = new Set(['site_regen', 'atf_cards', 'site_redraw']);

/** Whether a job belongs on the creatives surfaces (banner cards, the history list, the family refetch
 *  watchers, the banner failure toast). Art Style jobs (sample draws, extractions) and the website-art jobs are
 *  excluded: the pages that start them track them themselves, and none of them is a banner run. Pure. */
export const isCreativesSurfaceJob = (job: CreativeJob): boolean =>
  job.kind !== ART_STYLE_SAMPLE_KIND && job.kind !== ART_STYLE_EXTRACT_KIND && !WEBSITE_ART_JOB_KINDS.has(job.kind ?? '');

/** The jobs map's values, minus the Art Style jobs (see {@link isCreativesSurfaceJob}). Pure,
 *  memoise at the call site. */
export const creativesSurfaceJobs = (byId: Record<string, CreativeJob>): CreativeJob[] =>
  Object.values(byId).filter(isCreativesSurfaceJob);

/** In-flight runs keyed by the banner they target (root_banner_id → job kind), so a banner card
 *  can show a busy overlay while it's regenerating or publishing. Pure — memoise at the call site. */
export const activeRunsByRoot = (jobs: CreativeJob[]): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const j of jobs) {
    if ((j.status === 'queued' || j.status === 'running') && j.root_banner_id) {
      out[j.root_banner_id] = j.kind || 'generate';
    }
  }
  return out;
};

/** Per-format progress of the in-flight multi-slot draw for each banner (root_banner_id →
 *  {completed, total}). The backend streams + persists `completed/total` as each format lands, so this
 *  drives a real, reload-safe progress bar. Only runs actually reporting formats (total > 0) appear;
 *  fast single-image runs (publish) are absent. Pure — memoise at the call site. */
export const activeGenerateProgressByRoot = (jobs: CreativeJob[]): Record<string, { completed: number; total: number }> => {
  const out: Record<string, { completed: number; total: number }> = {};
  for (const j of jobs) {
    if ((j.status === 'queued' || j.status === 'running') && j.root_banner_id && (j.total ?? 0) > 0) {
      out[j.root_banner_id] = { completed: j.completed ?? 0, total: j.total ?? 0 };
    }
  }
  return out;
};

/** The VERSION each in-flight 'formats' run is completing (root_banner_id → parent_id), so the editor
 *  can anchor the in-thumb generating overlay to the exact version being filled — reliable across a page
 *  refresh (the job's parent_id is persisted + replayed), unlike guessing from which version looks
 *  incomplete. Only active (queued / running) formats jobs with a parent_id appear. Pure — memoise at the
 *  call site. */
export const activeFormatsVersionByRoot = (jobs: CreativeJob[]): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const j of jobs) {
    if ((j.status === 'queued' || j.status === 'running') && j.kind === 'formats' && j.root_banner_id && j.parent_id) {
      out[j.root_banner_id] = j.parent_id;
    }
  }
  return out;
};

/** All jobs, newest first (convenience selector; recomputes each call — prefer the map + memo
 *  in always-mounted components). */
export const selectCreativeJobs = (state: RootState): CreativeJob[] => sortJobsNewestFirst(state.creativeJobs.byId);

/** In-flight jobs (queued / running). */
export const selectActiveCreativeJobs = (state: RootState): CreativeJob[] => activeJobs(selectCreativeJobs(state));

/** Whether any job is in flight (queued / running). A boolean, so an always-mounted subscriber re-renders only when
 *  it flips, not on every progress push. */
export const selectHasActiveCreativeJobs = (state: RootState): boolean =>
  Object.values(state.creativeJobs.byId).some((j) => j.status === 'queued' || j.status === 'running');

/**
 * Job progress arrives as `creative_job` websocket pushes, which are best-effort: one can be dropped while the
 * socket stays connected (a reconnect already re-reads the list). While a job is in flight and nothing in the jobs
 * store has changed for this long (no push AND no re-read), the list is re-read, so a dropped push, above all a
 * final `done`, is caught. Long steps (a video model call) can be silent for a minute or two: one list read each.
 */
export const JOBS_STALL_MS = 60_000;

/** How often an in-flight job checks whether the stream has stalled (well under JOBS_STALL_MS). */
export const JOBS_STALL_CHECK_MS = 15_000;

/** Whether the jobs stream has been silent for a full stall window: no update since `lastUpdateAt` and no re-read
 *  since `lastRereadAt` (0 when none yet), so re-reads are spaced at least one window apart. */
export const jobsStalled = (now: number, lastUpdateAt: number, lastRereadAt: number, stallMs = JOBS_STALL_MS): boolean =>
  now - Math.max(lastUpdateAt, lastRereadAt) >= stallMs;

/** Whether ANY in-flight job has stalled, each against its OWN last change (`lastChangeAts`, one per in-flight job),
 *  so another job's pushes never hide a run whose own push (e.g. its final `done`) was dropped. */
export const anyJobStalled = (
  now: number,
  lastChangeAts: number[],
  lastRereadAt: number,
  stallMs = JOBS_STALL_MS,
): boolean => lastChangeAts.some((t) => jobsStalled(now, t, lastRereadAt, stallMs));

export default creativeJobsSlice.reducer;
